// Centralized offer mapping utility
export interface OfferData {
  url: string;
  name: string;
  is_public?: boolean;
}

export interface OfferMappingState {
  offers: Map<string, string>; // url -> name mapping
  activeOffers: string[]; // list of active offer URLs
}

class OfferMappingService {
  private static instance: OfferMappingService;
  private offerMap: Map<string, string> = new Map();
  private activeOfferUrls: string[] = [];
  private publicOfferUrls: string[] = [];
  private listeners: Set<() => void> = new Set();

  private constructor() {}

  static getInstance(): OfferMappingService {
    if (!OfferMappingService.instance) {
      OfferMappingService.instance = new OfferMappingService();
    }
    return OfferMappingService.instance;
  }

  // Update the offer mapping from API response
  updateOffers(activeForms: OfferData[]): void {
    this.offerMap.clear();
    this.activeOfferUrls = [];
    this.publicOfferUrls = [];

    activeForms.forEach((form) => {
      this.offerMap.set(form.url, form.name);
      this.activeOfferUrls.push(form.url);
      if (form.is_public) {
          this.publicOfferUrls.push(form.url);
      }
    });

    // Notify all listeners
    this.notifyListeners();
  }

  // Get app name by URL
  getAppName(url: string): string {
    return this.offerMap.get(url) || `Offer ${url}`;
  }

  // Check if offer is active
  isOfferActive(url: string): boolean {
    return this.activeOfferUrls.includes(url);
  }

  // Check if offer is public
  isOfferPublic(url: string): boolean {
    return this.publicOfferUrls.includes(url);
  }

  // Get all active offer URLs
  getActiveOfferUrls(): string[] {
    return [...this.activeOfferUrls];
  }

  // Get all offers with names
  getAllOffers(): Map<string, string> {
    return new Map(this.offerMap);
  }

  // Subscribe to changes
  subscribe(callback: () => void): () => void {
    this.listeners.add(callback);
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((callback) => callback());
  }
}

export const offerMappingService = OfferMappingService.getInstance();
